import { useCallback } from "react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { permissionDeniedMessage, type DeniedAction } from "@/lib/permissionDenied";

/**
 * Refuse an action a member's project permissions do not allow — with a toast
 * that says what was refused and to ask someone for access, instead of a
 * disabled control or a modal that would fail on save. The app's one toaster
 * sits above the chat pill, so the message lands where the member is already
 * looking.
 *
 * Call it from the action's handler in place of opening anything:
 *
 *   if (!canManagePMOnProject(projectId)) return denyPermission("manageIssues", projectId);
 *
 * `projectId` null means no single project is in play (building view with none
 * the member may act in), and the message says "in any of your projects".
 * Repeated presses reuse one toast (keyed per action and project).
 */
export function usePermissionDenied() {
  const { t } = useLanguage();
  const copy = t.common.permissions;

  return useCallback(
    (action: DeniedAction, projectId: number | null): void => {
      const { title, description } = permissionDeniedMessage(
        copy,
        action,
        projectId === null ? "anyProject" : "project"
      );
      toast.warning(title, {
        id: `permission-denied:${action}:${projectId ?? "any"}`,
        description,
      });
    },
    [copy]
  );
}

/**
 * Run an action if the member may do it here, or refuse it with
 * `usePermissionDenied`'s message.
 *
 * With a project in play the check is that project's. With none (building
 * view), the action may go ahead when the member may do it in at least one of
 * `scopeProjectIds` — the thing it opens then offers only those projects.
 */
export function useProjectActionGuard(
  action: DeniedAction,
  allowed: (projectId: number) => boolean,
  scopeProjectIds: readonly number[]
) {
  const denyPermission = usePermissionDenied();
  return useCallback(
    (projectId: number | null, run: () => void): void => {
      const permitted =
        projectId !== null ? allowed(projectId) : scopeProjectIds.some((id) => allowed(id));
      if (permitted) {
        run();
        return;
      }
      denyPermission(action, projectId);
    },
    [action, allowed, denyPermission, scopeProjectIds]
  );
}

/**
 * Open the model viewer for a project's IFC, or refuse with the toast when the
 * member may not use it there. A model with no project is not a per-project
 * question, so it opens (the viewer's route guard still answers for the page).
 *
 * USE_MODEL_VIEWER (like VIEW_DRAWING_COMPARE for the drawing viewer) is an
 * app feature gate on the VIEWER, not a data boundary: the database deliberately
 * does not check it, so the chatbot keeps reading IFC/DWG data wherever the
 * member can reach the file. Never gate chat answers or chat attachments on
 * these switches, and word refusals as "use the viewer", not "see the model".
 */
export function useModelViewerGuard() {
  const { canUseModelViewerOnProject } = usePermissions();
  const denyPermission = usePermissionDenied();
  return useCallback(
    (projectId: number | null | undefined, run: () => void): void => {
      if (projectId != null && !canUseModelViewerOnProject(projectId)) {
        denyPermission("useModelViewer", projectId);
        return;
      }
      run();
    },
    [canUseModelViewerOnProject, denyPermission]
  );
}
